import { useEffect } from "react";
import { isArrayValue } from "@smartsuite/fields-logic/lib/safe-field-value/type-checking/arrays";

import { toMultipleValue } from "../helpers/multipleValue";

/**
 * Returns a "multiple entries" field's value in the array shape its control expects, and repairs
 * the value held in Formik when it is not already an array.
 *
 * Normalizing only for rendering is not enough. The scalar stays in Formik state, so the field's
 * `yup.array()` validator rejects it on submit with a raw, untranslated "this must be a `array`
 * type" and the respondent cannot submit a form they never touched (BUG-21362, BUG-21363).
 * Writing the array back also self-heals the poisoned localStorage checkpoint on the next autosave.
 *
 * `undefined` is the shape of an untouched field and `yup.array()` accepts it, so it is left alone;
 * every other non-array value (a scalar from a restored checkpoint, a URL prefill or an
 * internal-form record, plus `null` and `""`) fails validation and is repaired.
 */
export function useNormalizedMultipleValue<TValue>(
  value: unknown,
  onChange: (value: TValue[]) => void
): TValue[] {
  const normalizedValue = toMultipleValue<TValue>(value);
  const needsRepair = value !== undefined && !isArrayValue(value);

  useEffect(() => {
    if (needsRepair) {
      onChange(normalizedValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsRepair]);

  return normalizedValue;
}
